import api from "./axios";

export interface ReportCard {
    id: string;
    student_id: string;
    semester_id: string;
    class_id: string;
    final_score?: number | null;
    assignment_average?: number | null;
    exam_average?: number | null;
    attendance_percentage?: number | null;
    teacher_comments?: string | null;
    principal_comments?: string | null;
    rank_in_class?: string | null;
    total_students?: number | null;
    behavior_grade?: string | null;
    is_published: boolean;
    published_at?: string | null;
    created_at: string;
    updated_at: string;

    // Relationships
    student?: {
        id: string;
        nisn: string;
        user?: {
            id: string;
            name: string;
        };
    };
    semester?: {
        id: string;
        name: string;
        academic_year_id?: string;
        academicYear?: {
            id: string;
            name: string;
        };
    };
    class?: {
        id: string;
        name: string;
        grade_level: string;
    };
    published_by_teacher?: {
        id: string;
        user?: {
            id: string;
            name: string;
        };
    };

    // Computed attributes
    grade?: string;
    grade_description?: string;
    gpa?: number;
}

export interface ReportCardSettings {
    id: string;
    assignment_weight: string | number;
    exam_weight: string | number;
    attendance_weight: string | number;
    updated_by?: string | null;
}

export interface ReportCardFilters {
    class_id?: string | null;
    semester_id?: string | null;
    is_published?: boolean | null;
    search?: string;
    page?: number;
    per_page?: number;
}

const reportCardApi = {
    async getOptions(): Promise<{
        success: boolean;
        message: string;
        data: {
            classes: Array<{ id: string; name: string; grade_level: string }>;
            semesters: Array<{
                id: string;
                name: string;
                academic_year: { id: string; name: string } | null;
            }>;
        };
    }> {
        const response = await api.get("/v1/report-cards/options");
        return response.data;
    },

    async getAll(filters: ReportCardFilters = {}): Promise<{
        success: boolean;
        message: string;
        data: { items: ReportCard[]; meta: any };
    }> {
        const response = await api.get("/v1/report-cards", { params: filters });
        return response.data;
    },

    async getById(id: string): Promise<ReportCard> {
        const response = await api.get(`/v1/report-cards/${id}`);
        return response.data.data;
    },

    async generate(classId: string, semesterId: string): Promise<ReportCard[]> {
        const response = await api.post("/v1/report-cards/generate", {
            class_id: classId,
            semester_id: semesterId,
        });
        return response.data.data || [];
    },

    async update(
        id: string,
        data: {
            teacher_comments?: string;
            principal_comments?: string;
            behavior_grade?: string;
        },
    ): Promise<ReportCard> {
        const response = await api.put(`/v1/report-cards/${id}`, data);
        return response.data.data;
    },

    async publish(id: string): Promise<ReportCard> {
        const response = await api.post(`/v1/report-cards/${id}/publish`);
        return response.data.data;
    },

    async unpublish(id: string): Promise<ReportCard> {
        const response = await api.post(`/v1/report-cards/${id}/unpublish`);
        return response.data.data;
    },

    getPdfStreamUrl(id: string): string {
        return `${import.meta.env.VITE_API_BASE_URL || "/api"}/v1/report-cards/${id}/pdf`;
    },

    async getSettings(): Promise<ReportCardSettings> {
        const response = await api.get("/v1/report-cards/settings");
        return response.data.data;
    },

    async updateSettings(data: {
        assignment_weight: number;
        exam_weight: number;
        attendance_weight: number;
    }): Promise<ReportCardSettings> {
        const response = await api.put("/v1/report-cards/settings", data);
        return response.data.data;
    },

    async getMy(): Promise<ReportCard[]> {
        const response = await api.get("/v1/report-cards/my");
        return response.data.data || [];
    },
};

export default reportCardApi;
